import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { getDateLocale } from '../../i18n';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BottomNav } from '../../components/BottomNav/BottomNav';
import type { MyNotification } from '../../services/api';
import {
  refreshNotifications,
  markAsRead,
  markAllAsRead,
  isRead,
} from '../../services/notifications';
import { useNotifications } from '../../hooks/useNotifications';
import { usePullToRefresh, PullIndicator } from '../../hooks/usePullToRefresh';
import { PageSpinner } from '../../components/PageSpinner/PageSpinner';
import './NotificationsPage.css';
import { isEpisUrl, openEpis } from '../../services/episSso';

// Ventana fija de 48h. La maneja el servicio compartido (refreshNotifications
// llama al back con hours=48). Si en el futuro se quiere variable, se mueve
// el parámetro al servicio.
const HOURS_WINDOW = 48;

// Misma lógica de normalización que el SW (sw.ts:normalizePushUrl). Si
// añades un alias aquí, añádelo también allí — y viceversa. Mantener los
// dos sincronizados es manual a propósito: el SW corre en otro contexto
// y no puede importar módulos de la app.
function resolvePushUrl(raw: string | undefined | null): { internal?: string; external?: string } {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return {};
  if (/^https?:\/\//i.test(trimmed)) return { external: trimmed };
  const path = trimmed.startsWith('/') ? trimmed : '/' + trimmed;
  const lower = path.toLowerCase();
  if (/^\/detail\/[^/?#]+/.test(lower)) return { internal: path };
  if (lower === '/' || lower === '/home' || lower === '/cargas' || lower === '/positions') {
    return { internal: '/positions' };
  }
  if (lower === '/stats' || lower === '/estadisticas' || lower === '/estad%c3%adsticas') {
    return { internal: '/stats' };
  }
  if (lower === '/settings' || lower === '/ajustes' || lower === '/config') {
    return { internal: '/settings' };
  }
  return { internal: '/positions' };
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return i18n.t('notifications.now');
  if (min < 60) return i18n.t('notifications.minAgo', { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t('notifications.hoursAgo', { count: h });
  const d = Math.floor(h / 24);
  return i18n.t('notifications.daysAgo', { count: d });
}

function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const locale = getDateLocale();
  return d.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = useNotifications();
  const [loading, setLoading] = useState(items.length === 0);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setError(null);
    if (items.length === 0) setLoading(true);
    try {
      await refreshNotifications();
    } catch {
      setError(t('notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [items.length]);

  // Refrescamos al entrar a la pestaña (además del polling global), para
  // reflejar cualquier push recibido entre tics. Solo una vez por montaje.
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const pullState = usePullToRefresh(fetchData);

  const openTarget = (n: MyNotification) => {
    const target = resolvePushUrl(n.url);
    if (target.internal) {
      navigate(target.internal);
      return;
    }
    if (target.external && isEpisUrl(target.external)) {
      // Notificación de EPIs: se abre con acceso directo (sin repetir login).
      const standalone = window.matchMedia?.('(display-mode: standalone)').matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      openEpis(target.external, standalone);
      return;
    }
    if (target.external) {
      // Anchor click sintético: más permisivo con popup blockers que
      // window.open. En iOS PWA standalone abre Safari fuera de la PWA.
      try {
        const a = document.createElement('a');
        a.href = target.external;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        window.open(target.external, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleClick = (n: MyNotification) => {
    markAsRead(n.idNotificationLog);
    openTarget(n);
  };

  const unreadCount = items.reduce(
    (acc, n) => acc + (isRead(n.idNotificationLog) ? 0 : 1),
    0
  );

  return (
    <>
      <AppHeader showLogo title={t('notifications.title')} />
      <PullIndicator {...pullState} />
      <main className="notifications-page">
        <div className="notifications-page__top">
          <p className="notifications-page__hint">
            {t('notifications.window', { hours: HOURS_WINDOW })}
            {unreadCount > 0 && <> · <strong>{t('notifications.unread', { count: unreadCount })}</strong></>}
          </p>
          {unreadCount > 0 && (
            <button
              className="notifications-page__mark-all"
              onClick={() => markAllAsRead()}
            >
              {t('notifications.markAll')}
            </button>
          )}
        </div>

        {loading && items.length === 0 && <PageSpinner />}

        {!loading && error && items.length === 0 && (
          <div className="notifications-page__empty notifications-page__empty--error">{error}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="notifications-page__empty">
            {t('notifications.empty', { hours: HOURS_WINDOW })}
          </div>
        )}

        <ul className="notifications-list">
          {items.map((n) => {
            const target = resolvePushUrl(n.url);
            const clickable = !!(target.internal || target.external);
            const read = isRead(n.idNotificationLog);
            const onActivate = () => {
              if (clickable) handleClick(n);
              else markAsRead(n.idNotificationLog);
            };
            return (
              <li
                key={n.idNotificationLog}
                className={
                  'notifications-item' +
                  (clickable ? ' notifications-item--clickable' : '') +
                  (read ? ' notifications-item--read' : ' notifications-item--unread')
                }
                onClick={onActivate}
                role="button"
                tabIndex={0}
                onKeyDown={
                  (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }
                }
              >
                <div className="notifications-item__title-row">
                  <span className="notifications-item__title">{n.title}</span>
                  <span className="notifications-item__meta">
                    <span className="notifications-item__time" title={formatAbsolute(n.sentAt)}>
                      {formatRelative(n.sentAt)}
                    </span>
                    {!read && <span className="notifications-item__dot" aria-label={t('notifications.unreadDot')} />}
                  </span>
                </div>
                {n.body && <div className="notifications-item__body">{n.body}</div>}
                {clickable && (
                  <div className="notifications-item__link">
                    {target.external ? t('notifications.openLink') : t('notifications.goDetail')}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </main>
      <BottomNav />
    </>
  );
}
